import { cloneDeep } from "lodash";

/**
 * The school clock. A planned moment is a business rule in Dutch wall-clock
 * time: the scheduler closes a meeting when it is 09:00 in Amsterdam, not when
 * it is 09:00 on the viewer's device. Read and write through the helpers below
 * so both sides agree.
 */
export const DUTCH_TIME_ZONE = "Europe/Amsterdam";

/**
 * How the school clock reads an instant, as "YYYY-MM-DD HH:mm:ss": sliceable,
 * and parseable back as UTC. Fields are spelled out so ICU locale data cannot
 * change the shape.
 */
const schoolClockFormatter = new Intl.DateTimeFormat("sv-SE", {
  timeZone: DUTCH_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const pad = (value: string | number): string => String(value).padStart(2, "0");

export const toSchoolClock = (date: Date): string =>
  schoolClockFormatter.format(date);

/**
 * Inverse of {@link toSchoolClock}. Resolved by fixed point: read the wall clock
 * as if it were UTC, measure the zone's offset there, then measure again at the
 * corrected instant so a DST changeover uses the offset that holds that day.
 *
 * Returns an invalid Date for an unreal moment -- Date.parse rejects "25:00"
 * and month 13 rather than rolling them onto the next day.
 */
export const fromSchoolClock = (wallClock: string): Date => {
  const asUtc = (value: string): number =>
    Date.parse(`${value.replace(" ", "T")}Z`);

  const asIfUtc = asUtc(wallClock);

  if (Number.isNaN(asIfUtc)) {
    return new Date(NaN);
  }

  const offsetAt = (instant: number): number =>
    asUtc(toSchoolClock(new Date(instant))) - instant;

  return new Date(asIfUtc - offsetAt(asIfUtc - offsetAt(asIfUtc)));
};

/** "Y-m-d H:i(:s)" with no zone designator, as legacy endpoints still emit. */
const NAIVE_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2})?$/;

/**
 * Parse an API datetime into an instant, or null when absent or unparseable.
 *
 * Zoned values are instants already. A naive one has no zone, and `new Date()`
 * would read it in the *browser's* timezone -- silently moving a deadline for
 * anyone not on Dutch time -- so it is read as school wall clock instead.
 */
export const parseSchoolDateTime = (
  value: Date | string | number | null | undefined,
): Date | null => {
  // Not `!value`: 0 is a timestamp (the Unix epoch), not a missing value.
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const naive = typeof value === "string" ? NAIVE_DATETIME.exec(value) : null;

  const parsed =
    naive === null
      ? new Date(value)
      : fromSchoolClock(`${naive[1]} ${naive[2]}${naive[3] ?? ":00"}`);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * This will generate a date string in the format of YYYY/MM/DD
 * @param {any} date - The date to be formatted. Defaults to Date.now()
 * @returns {any} A date string in the format of YYYY/MM/DD
 */
export const getDateString = (
  date: Date | number | string = Date.now(),
): string => {
  const selectedDate = new Date(date);
  const [day, month, year] = [
    selectedDate.getDate().toString().padStart(2, "0"),
    (selectedDate.getMonth() + 1).toString().padStart(2, "0"),
    selectedDate.getFullYear(),
  ];

  return `${year}/${month}/${day}`;
};

/**
 * This will generate a date string from the format of YYYY-MM-DD
 * @param {any} date - The date to be formatted
 * @returns {any} A date string in the format of YYYY/MM/DD
 */
export const getDateStringFromQuasar = (date: unknown): string | undefined => {
  if (!date || typeof date !== "string") {
    return;
  }

  const [day, month, year] = date.split("-");

  return getDateString(`${year}/${month}/${day}`);
};

/**
 * This will generate a date string in the format of DD-MM-YYYY
 * @param {any} date - The date to be formatted. Defaults to Date.now()
 * @param includeTime
 * @returns {any} A date string in the format of DD-MM-YYYY
 */
export const toDutchDateString = (
  date: Date | number | string = Date.now(),
  includeTime = false,
): string => {
  // "YYYY-MM-DD HH:mm:ss" -> "DD-MM-YYYY[ HH:mm]"
  const clock = toSchoolClock(parseSchoolDateTime(date) ?? new Date(date));
  const dateString = `${clock.slice(8, 10)}-${clock.slice(5, 7)}-${clock.slice(0, 4)}`;

  return includeTime ? `${dateString} ${clock.slice(11, 16)}` : dateString;
};

/**
 * This will return a date object from a dutch date and time string
 * @param {any} dateString - In the format: DD-MM-YYYY
 * @param {any} timeString = "00:00"
 * @returns {any}
 */
export const fromDutchToIntlDate = (
  dateString: string,
  timeString = "00:00",
): Date | undefined => {
  if (!dateString || typeof dateString !== "string") {
    return;
  }

  timeString = timeString || "00:00";

  // Date parts
  const dateParts = dateString.split("-");
  const day = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  const year = parseInt(dateParts[2], 10);

  // The inverse of formatDateInput/formatTimeInput, used as a pair by the
  // planning form. Both must read school time or a device ahead of Amsterdam
  // loses a day on the round trip.
  return (
    composeDateTime(`${year}-${pad(month)}-${pad(day)}`, timeString) ??
    undefined
  );
};

/**
 * This function takes a dateTime string and outputs a Dutch date (DD-MM-YYYY)
 * and time (HH:MM) as a separate object.
 * @param {string} dateTime - The date and time string in ISO format.
 * @returns {object} An object containing the formatted date and time.
 */
export const fromIntlToDutchDateTime = (
  dateTime: Date | string | null | undefined,
): { date: string; time: string } | undefined => {
  if (!dateTime) {
    return;
  }

  const dateObj = parseSchoolDateTime(dateTime);

  if (dateObj === null) {
    return;
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: DUTCH_TIME_ZONE,
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: DUTCH_TIME_ZONE,
  };

  const date = new Intl.DateTimeFormat("nl-NL", dateOptions).format(dateObj);
  const time = new Intl.DateTimeFormat("nl-NL", timeOptions).format(dateObj);

  return { date, time };
};

/**
 * Check if two dates are on the same day
 * @param {any} a - The first date to compare, b - The second date to compare
 * @returns {any}
 */
export const isSameDay = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/**
 * Checks if the second time is later than the first time.
 *
 * @param {string} time1 - The first time string in 'HH:mm' format.
 * @param {string} time2 - The second time string in 'HH:mm' format.
 * @returns {boolean} True if the second time is later than the first time, false otherwise.
 */
export const isSecondTimeLater = (time1: string, time2: string): boolean => {
  // Regex pattern to match 'HH:mm' format where HH is 00-23 and mm is 00-59
  const timePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

  // Check if both time strings match the pattern
  if (![time1, time2].every((time) => timePattern.test(time))) {
    return false;
  }

  // Parse the first time string
  const [hours1, minutes1] = time1.split(":").map(Number);

  // Parse the second time string
  const [hours2, minutes2] = time2.split(":").map(Number);

  // Convert both times to total minutes since midnight
  const totalMinutes1 = hours1 * 60 + minutes1;
  const totalMinutes2 = hours2 * 60 + minutes2;

  // Compare the total minutes
  return totalMinutes2 > totalMinutes1;
};

/**
 * This will return a nice month and year string
 * @param {number} month
 * @param {number} year
 * @returns {any}
 */
export const getDateFromMonthYear = (month: number, year: number): string => {
  const date = new Date(year, month - 1);

  // Use Intl.DateTimeFormat to format the majority month and year
  return new Intl.DateTimeFormat("nl-NL", {
    month: "long",
    year: "numeric",
  }).format(date);
};

export const getWeekNumber = (date = new Date()): [number, number] => {
  const _date = cloneDeep(date);

  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  _date.setUTCDate(_date.getUTCDate() + 4 - (_date.getUTCDay() || 7));

  // Get first day of year
  const yearStart = new Date(Date.UTC(_date.getUTCFullYear(), 0, 1));

  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil(
    ((_date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );

  // Return array of year and week number
  return [weekNo, _date.getUTCFullYear()];
};

export const isISODateString = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }

  // ISO 8601 regex (strict for the "Z" UTC format)
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

  if (!isoRegex.test(value)) {
    return false;
  }

  const date = new Date(value);

  return !isNaN(date.getTime());
};

export const getDatesBetween = (
  startDate: Date | string | number,
  endDate: Date | string | number,
): Date[] => {
  if (!startDate || !endDate) {
    throw new Error("startDate and endDate are required");
  }

  const dates: Date[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  // Ensure the time portion doesn't interfere
  current.setHours(0, 0, 0, 0);

  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    dates.push(new Date(current));

    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/**
 * Formats a date to a Dutch long format string like
 * "woensdag 30 april 2025 om 16:34", or "woensdag 30 april 2025" when
 * `includeTime` is false.
 */
export const toDutchLongDateString = (
  date: Date | number | string,
  includeTime = true,
): string => {
  const dateObj = parseSchoolDateTime(date) ?? new Date(date);

  const formattedDate = new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: DUTCH_TIME_ZONE,
  }).format(dateObj);

  if (!includeTime) {
    return formattedDate;
  }

  const formattedTime = new Intl.DateTimeFormat("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: DUTCH_TIME_ZONE,
  }).format(dateObj);

  return `${formattedDate} om ${formattedTime}`;
};

/**
 * The Dutch weekday a date falls on, e.g. "maandag". Read in school time, so it
 * agrees with the date `formatLongDate()` shows for the same value — a deadline
 * late in the evening must not name one weekday and a different day's date.
 */
export const toDutchWeekdayString = (date: Date | number | string): string =>
  new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    timeZone: DUTCH_TIME_ZONE,
  }).format(parseSchoolDateTime(date) ?? new Date(date));

export const getTimeRemaining = (
  deadline: Date,
): { days: string; hours: string; minutes: string } => {
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();

  if (diff <= 0) {
    return { days: "0 dagen", hours: "0 uur", minutes: "0 minuten" };
  }

  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  return {
    days: `${days} ${days === 1 ? "dag" : "dagen"}`,
    hours: `${hours} uur`,
    minutes: `${minutes} ${minutes === 1 ? "minuut" : "minuten"}`,
  };
};

/**
 * Format a Date as "YYYY-MM-DD" in school time, suitable for `<input type="date">`.
 * Returns "" when no date is provided.
 */
export const formatDateInput = (date: Date | null): string => {
  if (!date) {
    return "";
  }
  return toSchoolClock(date).slice(0, 10);
};

/**
 * Format a Date as "HH:mm" in school time, suitable for `<input type="time">`.
 * Returns `fallback` when no date is provided.
 */
export const formatTimeInput = (
  date: Date | null,
  fallback: string = "",
): string => {
  if (!date) {
    return fallback;
  }
  return toSchoolClock(date).slice(11, 16);
};

/**
 * Build a Date from a "YYYY-MM-DD" date + "HH:mm" time pair, read as school
 * time so it round-trips with formatDateInput/formatTimeInput. Returns null
 * when no date is provided. Falls back to midnight when the time is empty.
 */
export const composeDateTime = (
  dateStr: string,
  timeStr: string,
): Date | null => {
  if (!dateStr) {
    return null;
  }

  // Padding an empty part would turn ":" into a valid midnight, so require the
  // HH:mm shape up front rather than normalising whatever arrives.
  const time = /^(\d{1,2}):(\d{2})$/.exec(timeStr || "00:00");

  if (time === null) {
    return null;
  }

  const wanted = `${dateStr} ${pad(time[1])}:${time[2]}:00`;
  const composed = fromSchoolClock(wanted);

  // If the school clock does not read back exactly what was asked for, the
  // input was not a real moment ("2026-02-31", "25:00", "09:60").
  return Number.isNaN(composed.getTime()) || toSchoolClock(composed) !== wanted
    ? null
    : composed;
};

/**
 * Return a new Date seven days after the given date.
 */
export const addOneWeekToDate = (date: Date): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + 7);
  return next;
};

/**
 * Dutch long date without weekday, e.g. "30 april 2025".
 */
export const formatLongDate = (date: Date | number | string): string =>
  new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: DUTCH_TIME_ZONE,
  }).format(parseSchoolDateTime(date) ?? new Date(date));

/**
 * Dutch long date with time, e.g. "30 april 2025 16:34".
 */
export const formatLongDateTime = (date: Date | number | string): string =>
  new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: DUTCH_TIME_ZONE,
  }).format(parseSchoolDateTime(date) ?? new Date(date));

/**
 * Return a new Date seven days before the given date.
 */
export const subtractOneWeekFromDate = (date: Date): Date => {
  const previous = new Date(date);
  previous.setDate(previous.getDate() - 7);
  return previous;
};

/**
 * Last day of the NEXT school year.
 *
 * School years run 1 August - 31 July, so from August onwards the next school
 * year ends on 31 July two calendar years out. Mirrors the backend planning
 * window (@invariant MEETING-TRANSITION-PLANNING-WINDOW-001).
 */
export const getNextSchoolYearEnd = (
  date: Date | number | string = Date.now(),
): Date => {
  const selectedDate = new Date(date);
  const currentSchoolYearEndYear =
    selectedDate.getMonth() + 1 >= 8
      ? selectedDate.getFullYear() + 1
      : selectedDate.getFullYear();

  return new Date(currentSchoolYearEndYear + 1, 6, 31);
};
